import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";

/**
 * Fields type definition for content type 'TypeTile'
 * @name TypeTileFields
 * @type {TypeTileFields}
 * @memberof TypeTile
 */
export interface TypeTileFields {
    /**
     * Field type definition for field 'name' (Name)
     * @name Name
     * @localized false
     */
    name: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'variation' (Variation)
     * @name Variation
     * @localized false
     */
    variation?: EntryFieldTypes.Symbol<"badge" | "default" | "divider">;
    /**
     * Field type definition for field 'image' (Image)
     * @name Image
     * @localized false
     */
    image?: EntryFieldTypes.AssetLink;
    /**
     * Field type definition for field 'image_config' (Image Config)
     * @name Image Config
     * @localized false
     */
    image_config?: EntryFieldTypes.Object;
    /**
     * Field type definition for field 'image_aspect_ratio' (Image Aspect Ratio (Deprecated))
     * @name Image Aspect Ratio (Deprecated)
     * @localized false
     */
    image_aspect_ratio?: EntryFieldTypes.Symbol<"1/1" | "12/5" | "16/9" | "2/1" | "3/2" | "4/3" | "5/4">;
    /**
     * Field type definition for field 'iconSelection' (Icon Selection)
     * @name Icon Selection
     * @localized false
     */
    iconSelection?: EntryFieldTypes.Object;
    /**
     * Field type definition for field 'icon' (Icon (Deprecated))
     * @name Icon (Deprecated)
     * @localized false
     */
    icon?: EntryFieldTypes.Symbol<"activity" | "airplay" | "alert-circle" | "alert-octagon" | "alert-triangle" | "align-center" | "align-justify" | "align-left" | "align-right" | "anchor" | "aperture" | "archive" | "arrow-down" | "arrow-down-circle" | "arrow-down-left" | "arrow-down-right" | "arrow-left" | "arrow-left-circle" | "arrow-right" | "arrow-right-circle" | "arrow-up" | "arrow-up-circle" | "arrow-up-left" | "arrow-up-right" | "at-sign" | "award" | "bar-chart" | "bar-chart-2" | "battery" | "battery-charging" | "bell" | "bell-off" | "bluetooth" | "bold" | "book" | "book-open" | "bookmark" | "box" | "briefcase" | "calendar" | "camera" | "camera-off" | "cast" | "check" | "check-circle" | "check-square" | "chevron-down" | "chevron-left" | "chevron-right" | "chevron-up" | "chevrons-down" | "chevrons-left" | "chevrons-right" | "chevrons-up" | "chrome" | "circle" | "clipboard" | "clock" | "cloud" | "cloud-drizzle" | "cloud-lightning" | "cloud-off" | "cloud-rain" | "cloud-snow" | "code" | "codepen" | "codesandbox" | "coffee" | "columns" | "command" | "compass" | "copy" | "corner-down-left" | "corner-down-right" | "corner-left-down" | "corner-left-up" | "corner-right-down" | "corner-right-up" | "corner-up-left" | "corner-up-right" | "cpu" | "credit-card" | "crop" | "crosshair" | "database" | "delete" | "disc" | "divide" | "divide-circle" | "divide-square" | "dollar-sign" | "download" | "download-cloud" | "dribbble" | "droplet" | "edit" | "edit-2" | "edit-3" | "external-link" | "eye" | "eye-off" | "facebook" | "fast-forward" | "feather" | "figma" | "file" | "file-minus" | "file-plus" | "file-text" | "film" | "filter" | "flag" | "folder" | "folder-minus" | "folder-plus" | "framer" | "frown" | "gift" | "git-branch" | "git-commit" | "git-merge" | "git-pull-request" | "github" | "gitlab" | "globe" | "grid" | "hard-drive" | "hash" | "headphones" | "heart" | "help-circle" | "hexagon" | "home" | "image" | "inbox" | "info" | "instagram" | "italic" | "key" | "layers" | "layout" | "life-buoy" | "link" | "link-2" | "linkedin" | "list" | "loader" | "lock" | "log-in" | "log-out" | "mail" | "map" | "map-pin" | "maximize" | "maximize-2" | "meh" | "menu" | "message-circle" | "message-square" | "mic" | "mic-off" | "minimize" | "minimize-2" | "minus" | "minus-circle" | "minus-square" | "monitor" | "moon" | "more-horizontal" | "more-vertical" | "mouse-pointer" | "move" | "music" | "navigation" | "navigation-2" | "octagon" | "package" | "paperclip" | "pause" | "pause-circle" | "pen-tool" | "percent" | "phone" | "phone-call" | "phone-forwarded" | "phone-incoming" | "phone-missed" | "phone-off" | "phone-outgoing" | "pie-chart" | "play" | "play-circle" | "plus" | "plus-circle" | "plus-square" | "pocket" | "power" | "printer" | "radio" | "refresh-ccw" | "refresh-cw" | "repeat" | "rewind" | "rotate-ccw" | "rotate-cw" | "rss" | "save" | "scissors" | "search" | "send" | "server" | "settings" | "share" | "share-2" | "shield" | "shield-off" | "shopping-bag" | "shopping-cart" | "shuffle" | "sidebar" | "skip-back" | "skip-forward" | "slack" | "slash" | "sliders" | "smartphone" | "smile" | "speaker" | "square" | "star" | "stop-circle" | "sun" | "sunrise" | "sunset" | "table" | "tablet" | "tag" | "target" | "terminal" | "thermometer" | "thumbs-down" | "thumbs-up" | "toggle-left" | "toggle-right" | "tool" | "trash" | "trash-2" | "trello" | "trending-down" | "trending-up" | "triangle" | "truck" | "tv" | "twitch" | "twitter" | "type" | "umbrella" | "underline" | "unlock" | "upload" | "upload-cloud" | "user" | "user-check" | "user-minus" | "user-plus" | "user-x" | "users" | "video" | "video-off" | "voicemail" | "volume" | "volume-1" | "volume-2" | "volume-x" | "watch" | "wifi" | "wifi-off" | "wind" | "x" | "x-circle" | "x-octagon" | "x-square" | "youtube" | "zap" | "zap-off" | "zoom-in" | "zoom-out">;
    /**
     * Field type definition for field 'icon_size' (Icon Size (Deprecated))
     * @name Icon Size (Deprecated)
     * @localized false
     */
    icon_size?: EntryFieldTypes.Symbol<"lg" | "md" | "sm" | "xl">;
    /**
     * Field type definition for field 'icon_color' (Icon Color (Deprecated))
     * @name Icon Color (Deprecated)
     * @localized false
     */
    icon_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'eyebrow' (Eyebrow)
     * @name Eyebrow
     * @localized false
     */
    eyebrow?: EntryFieldTypes.RichText;
    /**
     * Field type definition for field 'eyebrow_color' (Eyebrow Color)
     * @name Eyebrow Color
     * @localized false
     */
    eyebrow_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'heading' (Heading)
     * @name Heading
     * @localized false
     */
    heading?: EntryFieldTypes.RichText;
    /**
     * Field type definition for field 'heading_color' (Heading Color)
     * @name Heading Color
     * @localized false
     */
    heading_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'subhead' (Subhead)
     * @name Subhead
     * @localized false
     */
    subhead?: EntryFieldTypes.RichText;
    /**
     * Field type definition for field 'subhead_color' (Subhead Color)
     * @name Subhead Color
     * @localized false
     */
    subhead_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'body' (Body)
     * @name Body
     * @localized false
     */
    body?: EntryFieldTypes.RichText;
    /**
     * Field type definition for field 'body_color' (Body Color)
     * @name Body Color
     * @localized false
     */
    body_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'background_color' (Background Color)
     * @name Background Color
     * @localized false
     */
    background_color?: EntryFieldTypes.Symbol<"--surface-neutral-black" | "--surface-neutral-dark" | "--surface-neutral-light" | "--surface-neutral-mid" | "--surface-neutral-white" | "--surface-primary-1" | "--surface-primary-2" | "--surface-primary-3" | "--surface-primary-4" | "--surface-primary-5" | "--surface-primary-6">;
    /**
     * Field type definition for field 'skip_clone' (Skip Clone)
     * @name Skip Clone
     * @localized false
     * @summary Disable cloning of this entry via the Clone Two You Plugin
     */
    skip_clone?: EntryFieldTypes.Boolean;
}

/**
 * Entry skeleton type definition for content type 'tile' (Tile)
 * @name TypeTileSkeleton
 * @type {TypeTileSkeleton}
 * @author 4m1b9L6VfuUe5HiWpYWSiQ
 * @since 2024-07-10T16:11:32.287Z
 * @version 65
 */
export type TypeTileSkeleton = EntrySkeletonType<TypeTileFields, "tile">;
/**
 * Entry type definition for content type 'tile' (Tile)
 * @name TypeTile
 * @type {TypeTile}
 * @author 4m1b9L6VfuUe5HiWpYWSiQ
 * @since 2024-07-10T16:11:32.287Z
 * @version 65
 */
export type TypeTile<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeTileSkeleton, Modifiers, Locales>;

export function isTypeTile<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeTile<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'tile'
}

export type TypeTileWithoutLinkResolutionResponse = TypeTile<"WITHOUT_LINK_RESOLUTION">;
export type TypeTileWithoutUnresolvableLinksResponse = TypeTile<"WITHOUT_UNRESOLVABLE_LINKS">;
export type TypeTileWithAllLocalesResponse<Locales extends LocaleCode = LocaleCode> = TypeTile<"WITH_ALL_LOCALES", Locales>;
export type TypeTileWithAllLocalesAndWithoutLinkResolutionResponse<Locales extends LocaleCode = LocaleCode> = TypeTile<"WITHOUT_LINK_RESOLUTION" | "WITH_ALL_LOCALES", Locales>;
export type TypeTileWithAllLocalesAndWithoutUnresolvableLinksResponse<Locales extends LocaleCode = LocaleCode> = TypeTile<"WITHOUT_UNRESOLVABLE_LINKS" | "WITH_ALL_LOCALES", Locales>;
